import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import _ from "lodash";

import { useTheme } from "@mui/material/styles";

import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { CircularProgress, FormControl, Typography } from "@mui/material";

import AlertDialog from "../Dialogs/AlertDialog";

import HttpContext from "../../contexts/HTTP/HttpContext";
import ConfigContext from "../../contexts/Config/ConfigContext";

import useImageCompressor from "../../hooks/useImageCompresor";
import useServerUpload from "../../hooks/useServerUpload";

function Upload(props) {
  const theme = useTheme();
  const { post } = useContext(HttpContext);
  const { config } = useContext(ConfigContext);

  const { size, images, setImages, selectedModel, disabled } = props;

  const { compressImage } = useImageCompressor();
  const { handleServerImageUpload } = useServerUpload();

  const [uploading, setUploading] = useState(false);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showFailDialog, setShowFailDialog] = useState(false);
  const [failMessage, setFailMesage] = useState("");

  const [imageUploadLimit, setImageUploadLimit] = useState(2);

  const [highlighted, setHighlighted] = useState(false);

  const handleImageUpload = async (imageFiles) => {
    if (uploading) return;

    if (images.length + imageFiles.length >= imageUploadLimit) {
      setFailMesage(
        `You cannot upload more than ${imageUploadLimit} images at a time!`
      );
      setShowFailDialog(true);

      return;
    }

    const compressedImages = [];

    for (let imageFile of imageFiles) {
      const compressedImage = await compressImage(imageFile);
      if (!compressedImage) continue;

      compressedImages.push(compressedImage);
    }

    const imagesClone = _.cloneDeep(images);
    setImages([...imagesClone, ...compressedImages]);
  };

  const handleUploadToServer = async () => {
    if (_.isEmpty(images) || uploading) return;

    try {
      setUploading(true);

      const res = await handleServerImageUpload(images, selectedModel);

      if (res.status === 200) {
        setImages([]);
        setShowSuccessDialog(true);
      } else if (res.status === 413) {
        setShowFailDialog(true);
        setFailMesage(
          `You cannot upload more than ${imageUploadLimit} images at a time!`
        );
      } else {
        setShowFailDialog(true);
        setFailMesage(
          "Please verify that you are uploading images only, and that you have selected a model. If this error persists, please refresh the page"
        );
      }

      setUploading(false);
    } catch (error) {
      console.error("Upload error:", error);

      setUploading(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHighlighted(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
      return setHighlighted(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHighlighted(false);

    handleImageUpload(e.dataTransfer.files);
  };

  useEffect(() => {
    const imageUploadLimit = Number(_.get(config, "user.maxImageUpload", 2));

    setImageUploadLimit(imageUploadLimit);
  }, [config]);

  return (
    <div style={{ display: "flex" }}>
      <FormControl style={{ margin: theme.spacing(0.5) }}>
        <input
          accept="image/*"
          style={{ display: "none" }}
          id="raised-button-file"
          multiple={true}
          type="file"
          onChange={(e) => handleImageUpload(e.target.files)}
          disabled={uploading}
        />
        <label htmlFor="raised-button-file">
          <div
            style={{
              borderStyle: "dashed",
              borderColor: "lightgrey",
              borderWidth: "3px",
              borderRadius: Math.floor(size / 10),
              cursor: uploading ? null : "pointer",
              height: size,
              width: size,
              minHeight: "135px",
              minWidth: "135px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: highlighted ? "white" : null,
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <AddPhotoAlternateIcon
              style={{
                color: "lightgrey",
                fontSize: "48px",
              }}
            />
          </div>
        </label>
      </FormControl>
      <FormControl style={{ margin: theme.spacing(0.5) }}>
        <div
          style={{
            borderStyle: "solid",
            borderColor: "lightgrey",
            borderWidth: "3px",
            borderRadius: Math.floor(size / 10),
            cursor: uploading ? null : "pointer",
            height: size,
            width: size,
            minHeight: "135px",
            minWidth: "135px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={handleUploadToServer}
        >
          {uploading && <CircularProgress />}
          <div
            style={{
              display: uploading ? "none" : "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <VisibilityIcon
              style={{
                color: "lightgrey",
                fontSize: "48px",
              }}
            />
            <Typography variant="caption" style={{ color: "grey" }}>
              Process Images
            </Typography>
          </div>
        </div>
      </FormControl>
      <AlertDialog
        open={showFailDialog}
        handleClose={() => setShowFailDialog(false)}
        title="Image Upload Failed"
      >
        <Typography style={{ marginBottom: theme.spacing(2) }}>
          {failMessage}
        </Typography>
      </AlertDialog>
      <AlertDialog
        open={showSuccessDialog}
        handleClose={() => setShowSuccessDialog(false)}
        title="Image Processing Started"
      >
        <Typography style={{ marginBottom: theme.spacing(2) }}>
          {`Your images have been submitted successfully! The notification bell will let you know when your images have been processed.`}
        </Typography>
      </AlertDialog>
    </div>
  );
}

export default Upload;
